import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import {
	createRoutesFromChildren,
	matchRoutes,
	useLocation,
	useNavigationType,
} from 'react-router-dom';

const dsn = import.meta.env['VITE_SENTRY_DSN'];

const parseSampleRate = (value: string | undefined, fallback: number): number => {
	if (!value) return fallback;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : fallback;
};

/**
 * Boots the Sentry SDK. No-op in dev and when no DSN is set, so local and
 * preview builds stay silent. Error capture itself lives in `shared/logger.ts`;
 * this only wires the SDK and the React Router v7 (data router) integration.
 */
export const initSentry = (): void => {
	if (!import.meta.env.PROD || !dsn) return;

	Sentry.init({
		dsn,
		release: __APP_VERSION__,
		environment: import.meta.env['VITE_SENTRY_ENVIRONMENT'] || import.meta.env.MODE,
		integrations: [
			// Data-router flavor: pairs with wrapCreateBrowserRouter in the router.
			Sentry.reactRouterBrowserTracingIntegration({
				useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes,
			}),
		],
		// Drop errors whose stack never touches our bundle — e.g. scripts Chrome iOS
		// injects into the page, which report the page URL instead of an asset.
		allowUrls: [`${window.location.origin}/assets/`],
		// Kept low by default to protect the free-tier quota; override via env.
		tracesSampleRate: parseSampleRate(import.meta.env['VITE_SENTRY_TRACES_SAMPLE_RATE'], 0.1),
	});
};
